import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ButtonWithSpinner from "../ButtonWithSpinner";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import {
  formatInternalTicketProtocol,
  getInternalTicketPriorityLabel,
  getInternalTicketSlaLabel,
  getInternalTicketStatusLabel,
  formatInternalTicketCreation
} from "../../helpers/internalTicket";

const useStyles = makeStyles(theme => ({
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1.5)
  },
  metaCard: {
    padding: theme.spacing(1.5),
    borderRadius: 14,
    marginBottom: theme.spacing(2),
    background:
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.08)"
        : "rgba(148, 163, 184, 0.08)"
  },
  eventsList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  eventCard: {
    padding: theme.spacing(1.25),
    borderRadius: 12
  },
  eventTitle: {
    fontWeight: 700
  },
  eventMeta: {
    color: theme.palette.text.secondary,
    fontSize: "0.78rem"
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  quickCommentChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  commentActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(1)
  },
  finalCommentBox: {
    marginTop: theme.spacing(1)
  },
  eventDiffList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.6),
    marginTop: theme.spacing(1)
  },
  eventDiffRow: {
    fontSize: "0.86rem",
    color: theme.palette.text.primary
  },
  eventDiffLabel: {
    fontWeight: 700
  }
}));

const defaultForm = {
  title: "",
  description: "",
  priority: "medium",
  status: "open",
  slaDueAt: "",
  assignedUserId: ""
};

const getQuickCommentOptions = () => [
  i18n.t("internalTickets.dialog.quickComments.customerReturn"),
  i18n.t("internalTickets.dialog.quickComments.thirdParty"),
  i18n.t("internalTickets.dialog.quickComments.technicalReview"),
  i18n.t("internalTickets.dialog.quickComments.waitingValidation"),
  i18n.t("internalTickets.dialog.quickComments.internalAlignment")
];

const InternalTicketDialog = ({
  open,
  onClose,
  ticketId,
  contactId,
  internalTicket,
  onSaved
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [ticketData, setTicketData] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [commentBody, setCommentBody] = useState("");
  const [finalComment, setFinalComment] = useState("");
  const [contactLoading, setContactLoading] = useState(false);
  const quickCommentOptions = useMemo(() => getQuickCommentOptions(), [i18n.language]);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearchParam, setContactSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [linkTicketId, setLinkTicketId] = useState("");

  const isLockedToTicket = Boolean(ticketId && !internalTicket?.id);
  const editingTicket = ticketData || internalTicket || null;
  const isEditing = Boolean(editingTicket?.id);
  const isMovingToDone =
    form.status === "done" && (!editingTicket || editingTicket.status !== "done");

  useEffect(() => {
    if (!open) return;

    const nextTicketData = internalTicket || null;
    const nextForm = nextTicketData
      ? {
          title: nextTicketData.title || "",
          description: nextTicketData.description || "",
          priority: nextTicketData.priority || "medium",
          status: nextTicketData.status || "open",
          slaDueAt: nextTicketData.slaDueAt
            ? new Date(nextTicketData.slaDueAt).toISOString().slice(0, 16)
            : "",
          assignedUserId: nextTicketData.assignedUserId || ""
        }
      : defaultForm;

    setTicketData(nextTicketData);
    setForm(nextForm);
    setCommentBody("");
    setFinalComment("");
    setContactSearchParam("");
    setContactOptions([]);
    setSelectedContact(
      nextTicketData?.contact || nextTicketData?.ticket?.contact || null
    );
    setLinkTicketId(
      nextTicketData?.ticketId ? String(nextTicketData.ticketId) : ticketId ? String(ticketId) : ""
    );
  }, [open, internalTicket, ticketId]);

  useEffect(() => {
    if (!open || isLockedToTicket || isEditing) return;
    if (contactSearchParam.trim().length < 2) {
      setContactLoading(false);
      return;
    }

    let active = true;
    setContactLoading(true);

    const debounce = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", {
          params: {
            searchParam: contactSearchParam.trim(),
            pageNumber: 1
          }
        });

        if (!active) return;

        setContactOptions(
          Array.isArray(data?.contacts) ? data.contacts : []
        );
      } catch (err) {
        if (active) {
          setContactOptions([]);
          toastError(err);
        }
      } finally {
        if (active) {
          setContactLoading(false);
        }
      }
    }, 400);

    return () => {
      active = false;
      clearTimeout(debounce);
    };
  }, [open, isLockedToTicket, isEditing, contactSearchParam]);

  useEffect(() => {
    if (!open || !ticketData?.id) return;

    let active = true;

    const loadInternalTicketDetails = async () => {
      setDetailsLoading(true);
      try {
        const { data } = await api.get(`/internal-tickets/${ticketData.id}`);
        if (active) {
          setTicketData(data);
        }
      } catch (err) {
        if (active) {
          toastError(err);
        }
      } finally {
        if (active) {
          setDetailsLoading(false);
        }
      }
    };

    loadInternalTicketDetails();

    return () => {
      active = false;
    };
  }, [open, ticketData?.id]);

  useEffect(() => {
    if (!open) return;

    let active = true;

    const loadUsers = async () => {
      setUsersLoading(true);
      try {
        // Sem pageSize vinham so os 20 usuarios mais novos.
        const { data } = await api.get("/users", {
          params: {
            transfer: "true",
            pageSize: 200,
            orderBy: "name"
          }
        });
        if (active) {
          setUsers(Array.isArray(data) ? data : data.users || []);
        }
      } catch (err) {
        if (active) {
          setUsers([]);
        }
      } finally {
        if (active) {
          setUsersLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      active = false;
    };
  }, [open]);

  const handleChange = field => event => {
    const nextValue = event?.target?.value ?? "";

    setForm(prev => ({
      ...prev,
      [field]: nextValue
    }));
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error(i18n.t("internalTickets.dialog.toasts.missingTitle"));
      return;
    }

    if (form.status === "done" && !form.assignedUserId) {
      toast.error(i18n.t("internalTickets.dialog.toasts.missingResponsible"));
      return;
    }

    if (isMovingToDone && !String(finalComment || "").trim()) {
      toast.error(i18n.t("internalTickets.dialog.toasts.missingFinalComment"));
      return;
    }

    const parsedTicketId = isLockedToTicket
      ? Number(ticketId)
      : Number(String(linkTicketId || "").trim());
    const normalizedTicketId = Number.isNaN(parsedTicketId) || !parsedTicketId
      ? null
      : parsedTicketId;
    const normalizedContactId = selectedContact?.id
      ? Number(selectedContact.id)
      : contactId
      ? Number(contactId)
      : null;

    if (!isEditing && !normalizedTicketId && !normalizedContactId) {
      toast.error(i18n.t("internalTickets.dialog.toasts.missingLink"));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ticketId: normalizedTicketId,
        contactId: normalizedContactId,
        title: form.title.trim(),
        description: form.description?.trim() || "",
        priority: form.priority,
        status: form.status,
        slaDueAt: form.slaDueAt || null,
        assignedUserId: form.assignedUserId || null,
        ...(form.status === "done"
          ? { finalComment: String(finalComment || "").trim() }
          : {})
      };

      const { data } = isEditing
        ? await api.put(`/internal-tickets/${editingTicket.id}`, payload)
        : await api.post("/internal-tickets", payload);

      setTicketData(data);
      toast.success(
        isEditing ? i18n.t("internalTickets.dialog.toasts.updated") : i18n.t("internalTickets.dialog.toasts.created")
      );
      if (onSaved) onSaved(data);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAppendQuickComment = text => {
    const normalizedText = String(text || "").trim();
    if (!normalizedText) return;

    setCommentBody(prev => (prev ? `${prev}\n${normalizedText}` : normalizedText));
  };

  const handleCreateComment = async () => {
    if (!isEditing || !editingTicket?.id) return;

    const normalizedBody = String(commentBody || "").trim();
    if (!normalizedBody) {
      toast.error(i18n.t("internalTickets.dialog.toasts.missingComment"));
      return;
    }

    setCommentLoading(true);
    try {
      const { data } = await api.post(
        `/internal-tickets/${editingTicket.id}/comments`,
        { body: normalizedBody }
      );

      setTicketData(data);
      setCommentBody("");
      if (onSaved) onSaved(data);
      toast.success(i18n.t("internalTickets.dialog.toasts.commentAdded"));
    } catch (err) {
      toastError(err);
    } finally {
      setCommentLoading(false);
    }
  };

  const events = useMemo(
    () => (editingTicket?.events ? editingTicket.events : []),
    [editingTicket]
  );

  const getContactOptionLabel = option => {
    if (!option) return "";
    const name = option.name || i18n.t("internalTickets.dialog.fields.noName");
    const number = option.number ? ` - ${option.number}` : "";
    return `${name}${number}`;
  };

  const parseEventPayload = event => {
    if (!event?.payload) {
      return null;
    }

    if (typeof event.payload === "string") {
      try {
        return JSON.parse(event.payload);
      } catch (error) {
        return null;
      }
    }

    return event.payload;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="internal-ticket-dialog-title"
    >
      <DialogTitle id="internal-ticket-dialog-title">
        {isEditing ? i18n.t("internalTickets.dialog.title.edit") : i18n.t("internalTickets.dialog.title.add")}
      </DialogTitle>
      <DialogContent dividers>
        {isEditing ? (
          <Paper variant="outlined" className={classes.metaCard}>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("internalTickets.dialog.meta.protocol")}
            </Typography>
            <Typography variant="h6">
              {formatInternalTicketProtocol(editingTicket)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("internalTickets.dialog.meta.client")}: {" "}
              {editingTicket?.contact?.name ||
                editingTicket?.ticket?.contact?.name ||
                i18n.t("internalTickets.dialog.meta.notLinked")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("internalTickets.dialog.meta.attendance")}: {" "}
              {editingTicket?.ticketId
                ? `#${String(editingTicket.ticketId).padStart(6, "0")}`
                : i18n.t("internalTickets.dialog.meta.notLinked")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {formatInternalTicketCreation(editingTicket, "internalTickets.dialog.meta")}
            </Typography>
            <div className={classes.chipsRow}>
              <Chip
                size="small"
                label={getInternalTicketStatusLabel(editingTicket.status)}
              />
              <Chip
                size="small"
                label={getInternalTicketPriorityLabel(editingTicket.priority)}
              />
              <Chip
                size="small"
                label={getInternalTicketSlaLabel(editingTicket.slaStatus)}
              />
            </div>
          </Paper>
        ) : null}

        <Grid container spacing={2}>
          {!isEditing ? (
            isLockedToTicket ? (
              <Grid item xs={12}>
                <TextField
                  label={i18n.t("internalTickets.dialog.fields.linkedTicket")}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  value={`#${String(ticketId).padStart(6, "0")}`}
                  disabled
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={i18n.t("internalTickets.dialog.fields.ticketId")}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    value={linkTicketId}
                    onChange={event => setLinkTicketId(event.target.value)}
                    helperText={i18n.t("internalTickets.dialog.helpers.ticketId")}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Autocomplete
                    options={contactOptions}
                    value={selectedContact}
                    loading={contactLoading}
                    onChange={(event, nextValue) => setSelectedContact(nextValue)}
                    onInputChange={(event, nextValue) =>
                      setContactSearchParam(nextValue || "")
                    }
                    getOptionLabel={getContactOptionLabel}
                    getOptionSelected={(option, value) =>
                      Number(option?.id) === Number(value?.id)
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={i18n.t("internalTickets.dialog.fields.client")}
                        variant="outlined"
                        margin="dense"
                        helperText={i18n.t("internalTickets.dialog.helpers.client")}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {contactLoading ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Grid>
              </>
            )
          ) : null}
          <Grid item xs={12} md={8}>
            <TextField
              label={i18n.t("internalTickets.dialog.fields.title")}
              variant="outlined"
              margin="dense"
              fullWidth
              value={form.title}
              onChange={handleChange("title")}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label={i18n.t("internalTickets.dialog.fields.priority")}
              variant="outlined"
              margin="dense"
              fullWidth
              value={form.priority}
              onChange={handleChange("priority")}
            >
              <MenuItem value="low">{i18n.t("internalTickets.labels.priority.low")}</MenuItem>
              <MenuItem value="medium">{i18n.t("internalTickets.labels.priority.medium")}</MenuItem>
              <MenuItem value="high">{i18n.t("internalTickets.labels.priority.high")}</MenuItem>
              <MenuItem value="urgent">{i18n.t("internalTickets.labels.priority.urgent")}</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={i18n.t("internalTickets.dialog.fields.description")}
              variant="outlined"
              margin="dense"
              fullWidth
              multiline
              minRows={4}
              value={form.description}
              onChange={handleChange("description")}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label={i18n.t("internalTickets.dialog.fields.status")}
              variant="outlined"
              margin="dense"
              fullWidth
              value={form.status}
              onChange={handleChange("status")}
            >
              <MenuItem value="open">{i18n.t("internalTickets.labels.status.open")}</MenuItem>
              <MenuItem value="in_progress">{i18n.t("internalTickets.labels.status.in_progress")}</MenuItem>
              <MenuItem value="waiting">{i18n.t("internalTickets.labels.status.waiting")}</MenuItem>
              <MenuItem value="done">{i18n.t("internalTickets.labels.status.done")}</MenuItem>
              <MenuItem value="cancelled">{i18n.t("internalTickets.labels.status.cancelled")}</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="SLA"
              type="datetime-local"
              variant="outlined"
              margin="dense"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.slaDueAt}
              onChange={handleChange("slaDueAt")}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label={i18n.t("internalTickets.dialog.fields.responsible")}
              variant="outlined"
              margin="dense"
              fullWidth
              value={form.assignedUserId}
              onChange={handleChange("assignedUserId")}
              disabled={usersLoading}
            >
              <MenuItem value="">{i18n.t("internalTickets.dialog.fields.noResponsible")}</MenuItem>
              {users.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {form.status === "done" ? (
            <Grid item xs={12}>
              <TextField
                label={i18n.t("internalTickets.dialog.fields.finalComment")}
                placeholder={i18n.t("internalTickets.dialog.placeholders.finalComment")}
                variant="outlined"
                margin="dense"
                fullWidth
                multiline
                minRows={2}
                required={isMovingToDone}
                value={finalComment}
                onChange={event => setFinalComment(event.target.value)}
                helperText={
                  isMovingToDone
                    ? i18n.t("internalTickets.dialog.helpers.finalCommentRequired")
                    : i18n.t("internalTickets.dialog.helpers.finalCommentOptional")
                }
                className={classes.finalCommentBox}
              />
            </Grid>
          ) : null}
        </Grid>

        {isEditing ? (
          <Box mt={3}>
            <Typography className={classes.sectionTitle}>{i18n.t("internalTickets.dialog.sections.comments")}</Typography>
            <div className={classes.quickCommentChips}>
              {quickCommentOptions.map(option => (
                <Chip
                  key={option}
                  size="small"
                  label={option}
                  onClick={() => handleAppendQuickComment(option)}
                  clickable
                  variant="outlined"
                />
              ))}
            </div>
            <TextField
              label={i18n.t("internalTickets.dialog.fields.newComment")}
              variant="outlined"
              margin="dense"
              fullWidth
              multiline
              minRows={3}
              value={commentBody}
              onChange={event => setCommentBody(event.target.value)}
            />
            <div className={classes.commentActions}>
              <ButtonWithSpinner
                loading={commentLoading}
                onClick={handleCreateComment}
                variant="outlined"
                color="primary"
              >
                {i18n.t("internalTickets.dialog.buttons.addComment")}
              </ButtonWithSpinner>
            </div>
          </Box>
        ) : null}

        {isEditing ? (
          <Box mt={3}>
            <Typography className={classes.sectionTitle}>{i18n.t("internalTickets.dialog.sections.history")}</Typography>
            <div className={classes.eventsList}>
              {detailsLoading ? (
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("internalTickets.dialog.history.loading")}
                </Typography>
              ) : events.length ? (
                events.map(event => {
                  const eventPayload = parseEventPayload(event);
                  const eventChanges = Array.isArray(eventPayload?.changes)
                    ? eventPayload.changes
                    : [];

                  return (
                    <Paper
                      variant="outlined"
                      className={classes.eventCard}
                      key={event.id}
                    >
                      <Typography className={classes.eventTitle}>
                        {event.title}
                      </Typography>
                      <Typography className={classes.eventMeta}>
                        {`${event.user?.name || i18n.t("internalTickets.dialog.history.system")} - ${new Date(
                          event.createdAt
                        ).toLocaleString("pt-BR")}`}
                      </Typography>
                      {eventChanges.length ? (
                        <div className={classes.eventDiffList}>
                          {eventChanges.map((change, index) => (
                            <Typography
                              variant="body2"
                              key={`${event.id}-change-${change.field || index}`}
                              className={classes.eventDiffRow}
                            >
                              <span className={classes.eventDiffLabel}>
                                {change.label || change.field || i18n.t("internalTickets.dialog.history.field")}:
                              </span>{" "}
                              {change.from || i18n.t("internalTickets.dialog.history.empty")} {"->"} {change.to || i18n.t("internalTickets.dialog.history.empty")}
                            </Typography>
                          ))}
                        </div>
                      ) : event.description ? (
                        <Typography
                          variant="body2"
                          style={{ whiteSpace: "pre-line", marginTop: 8 }}
                        >
                          {event.description}
                        </Typography>
                      ) : null}
                    </Paper>
                  );
                })
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("internalTickets.dialog.history.noEvents")}
                </Typography>
              )}
            </div>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          {i18n.t("internalTickets.dialog.buttons.cancel")}
        </Button>
        <ButtonWithSpinner
          loading={loading}
          onClick={handleSubmit}
          variant="contained"
          color="primary"
        >
          {isEditing ? i18n.t("internalTickets.dialog.buttons.okEdit") : i18n.t("internalTickets.dialog.buttons.okAdd")}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default InternalTicketDialog;
